import React, { useCallback, useEffect, useRef, useState } from "react";
import { isElectronApp } from "@src/utils/envUtils";
import styles from "./WebPreview.module.scss";
import { ChevronLeft, ChevronRight, RotateCw } from "lucide-react";

interface WebPreviewProps {
  /** The URL to load in the preview */
  url: string;
  /** Optional callback fired when the URL changes due to in-app navigation (Electron only) */
  onUrlChange?: (url: string) => void;
  /** Height of the preview container. Defaults to "100%" */
  height?: string;
  /** Width of the preview container. Defaults to "100%" */
  width?: string;
  /** Show a navigation toolbar (back / forward / reload + page title). Defaults to true */
  showToolbar?: boolean;
  className?: string;
  isFromSubjectsLearning?: boolean;
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Electron <webview> panel (ported from the devOpsExe reference)             */
/* ─────────────────────────────────────────────────────────────────────────── */

function withWebview<T>(
  wv: HTMLElement | null,
  ready: boolean,
  fn: (el: any) => T,
): T | undefined {
  if (!wv || !ready) return undefined;
  try {
    return fn(wv);
  } catch {
    return undefined;
  }
}

const normalizeUrl = (u: string): string => {
  if (!u) return "";
  try {
    const parsed = new URL(u);
    let path = parsed.pathname;
    if (path.endsWith("/")) {
      path = path.slice(0, -1);
    }
    return `${parsed.protocol}//${parsed.host}${path}${parsed.search}`;
  } catch {
    return u.endsWith("/") ? u.slice(0, -1) : u;
  }
};

const ElectronWebView: React.FC<{
  url: string;
  onUrlChange?: (url: string) => void;
  showToolbar: boolean;
}> = ({ url, onUrlChange, showToolbar }) => {
  const webviewRef = useRef<HTMLElement | null>(null);
  const pendingUrlRef = useRef(url);
  const [webviewReady, setWebviewReady] = useState(false);
  const webviewReadyRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const [pageTitle, setPageTitle] = useState("");
  const [currentUrl, setCurrentUrl] = useState(url);

  const setWebviewReadyAndRef = useCallback((val: boolean) => {
    setWebviewReady(val);
    webviewReadyRef.current = val;
  }, []);

  const syncNavState = useCallback(() => {
    const wv = webviewRef.current;
    if (!wv || !webviewReadyRef.current) return;
    setCanGoBack(Boolean(withWebview(wv, true, (el) => el.canGoBack())));
    setCanGoForward(Boolean(withWebview(wv, true, (el) => el.canGoForward())));
  }, []);

  const applyUrl = useCallback((targetUrl: string) => {
    const wv = webviewRef.current;
    if (!wv || !targetUrl) return;
    pendingUrlRef.current = targetUrl;

    if (!webviewReadyRef.current) {
      if ((wv as any).getAttribute("src") !== targetUrl) {
        (wv as any).src = targetUrl;
      }
      return;
    }

    const current = withWebview(wv, true, (el) => el.getURL());
    if (current && current !== "about:blank") {
      if (normalizeUrl(current) !== normalizeUrl(targetUrl)) {
        (wv as any).loadURL(targetUrl);
      }
    } else {
      (wv as any).loadURL(targetUrl);
    }
  }, []);

  useEffect(() => {
    const wv = webviewRef.current;
    if (!wv) return;

    const onStart = () => setLoading(true);
    const onStop = () => {
      setLoading(false);
      syncNavState();
    };
    const onFailure = () => {
      setLoading(false);
    };
    const onTitle = (e: any) => setPageTitle(e.title || "");
    const onNavigate = (e: any) => {
      if (e.url) {
        pendingUrlRef.current = e.url;
        setCurrentUrl(e.url);
        onUrlChange?.(e.url);
      }
      syncNavState();
    };
    const onDomReady = () => {
      setLoading(false);
      setWebviewReadyAndRef(true);
      syncNavState();

      const current = withWebview(wv, true, (el) => el.getURL());
      if (current) {
        setCurrentUrl(current);
      }
    };

    wv.addEventListener("did-start-loading", onStart);
    wv.addEventListener("did-stop-loading", onStop);
    wv.addEventListener("page-title-updated", onTitle);
    wv.addEventListener("did-navigate", onNavigate);
    wv.addEventListener("did-navigate-in-page", onNavigate);
    wv.addEventListener("dom-ready", onDomReady);
    wv.addEventListener("did-fail-load", onFailure);
    wv.addEventListener("crashed", onFailure);

    return () => {
      setWebviewReadyAndRef(false);
      wv.removeEventListener("did-start-loading", onStart);
      wv.removeEventListener("did-stop-loading", onStop);
      wv.removeEventListener("page-title-updated", onTitle);
      wv.removeEventListener("did-navigate", onNavigate);
      wv.removeEventListener("did-navigate-in-page", onNavigate);
      wv.removeEventListener("dom-ready", onDomReady);
      wv.removeEventListener("did-fail-load", onFailure);
      wv.removeEventListener("crashed", onFailure);
    };
  }, [onUrlChange, syncNavState, setWebviewReadyAndRef]);

  useEffect(() => {
    setCurrentUrl(url);
    applyUrl(url);
  }, [url, applyUrl]);

  const goBack = () =>
    withWebview(webviewRef.current, webviewReadyRef.current, (wv) =>
      wv.goBack(),
    );
  const goForward = () =>
    withWebview(webviewRef.current, webviewReadyRef.current, (wv) =>
      wv.goForward(),
    );
  const reload = () =>
    withWebview(webviewRef.current, webviewReadyRef.current, (wv) =>
      wv.reload(),
    );

  return (
    <div className={styles.webviewWrapper}>
      {showToolbar && (
        <div className={styles.toolbar}>
          <div className={styles.toolbarNav}>
            <button
              type="button"
              className={styles.navBtn}
              onClick={goBack}
              disabled={!canGoBack}
              title="Back"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              className={styles.navBtn}
              onClick={goForward}
              disabled={!canGoForward}
              title="Forward"
            >
              <ChevronRight size={16} />
            </button>
            <button
              type="button"
              className={styles.navBtn}
              onClick={reload}
              title="Reload"
            >
              <RotateCw size={14} />
            </button>
          </div>
          <div className={styles.addressBar}>
            <input
              type="text"
              value={currentUrl}
              readOnly
              className={styles.addressInput}
              title={currentUrl}
            />
          </div>
        </div>
      )}

      <div className={styles.contentArea}>
        {loading && (
          <div className={styles.loadingOverlay} aria-hidden="true" />
        )}
        {/* @ts-expect-error: <webview> is an Electron-specific HTML tag */}
        <webview
          ref={webviewRef as any}
          className={styles.webview}
          partition="persist:webpreview"
          webpreferences="contextIsolation=yes, nodeIntegration=no, sandbox=yes"
          allowpopups={true}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* Browser <iframe> fallback                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

const IframePreview: React.FC<{
  url: string;
  showToolbar: boolean;
}> = ({ url, showToolbar }) => {
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState(0); // force re-mount on reload
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const reload = () => {
    setLoading(true);
    setKey((k) => k + 1);
  };

  const goBack = () => {
    try {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.history.back();
      }
    } catch (e) {
      console.error("Failed to navigate iframe back", e);
    }
  };

  const goForward = () => {
    try {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.history.forward();
      }
    } catch (e) {
      console.error("Failed to navigate iframe forward", e);
    }
  };

  return (
    <div className={styles.webviewWrapper}>
      {showToolbar && (
        <div className={styles.toolbar}>
          <div className={styles.toolbarNav}>
            <button
              type="button"
              className={styles.navBtn}
              onClick={goBack}
              title="Back"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              className={styles.navBtn}
              onClick={goForward}
              title="Forward"
            >
              <ChevronRight size={16} />
            </button>
            <button
              type="button"
              className={styles.navBtn}
              onClick={reload}
              title="Reload"
            >
              <RotateCw size={14} />
            </button>
          </div>
          <div className={styles.addressBar}>
            <input
              type="text"
              value={url}
              readOnly
              className={styles.addressInput}
              title={url}
            />
          </div>
        </div>
      )}

      <div className={styles.contentArea}>
        {loading && (
          <div className={styles.loadingOverlay} aria-hidden="true" />
        )}
        <iframe
          ref={iframeRef}
          key={key}
          src={url}
          className={styles.webview}
          title="Web Preview"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          onLoad={() => setLoading(false)}
          referrerPolicy="no-referrer"
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* Public component                                                            */
/* ─────────────────────────────────────────────────────────────────────────── */

const WebPreview: React.FC<WebPreviewProps> = ({
  url,
  onUrlChange,
  height = "100%",
  width = "100%",
  showToolbar = true,
  className = "",
  isFromSubjectsLearning = false,
}) => {
  const isElectron = isElectronApp();

  const targetUrl = isFromSubjectsLearning
    ? url &&
      (url.includes("3000") ||
        url.includes("9090") ||
        url.toLowerCase().includes("grafana") ||
        url.toLowerCase().includes("prometheus"))
      ? url
      : import.meta.env.VITE_DEVOPS_LOACL_URL || url
    : url;

  if (!targetUrl) {
    return (
      <div
        className={`${styles.container} ${className}`}
        style={{ height, width }}
      >
        <div className={styles.emptyState}>No URL provided</div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${isElectron ? styles.webviewMode : ""} ${className}`}
      style={{ height, width }}
    >
      {isElectron ? (
        <ElectronWebView
          url={targetUrl}
          onUrlChange={onUrlChange}
          showToolbar={showToolbar}
        />
      ) : (
        <IframePreview url={targetUrl} showToolbar={showToolbar} />
      )}
    </div>
  );
};

export default WebPreview;
